import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { usePlaceStatusCache } from '../contexts/PlaceStatusCacheContext';
import { useLists } from '../contexts/ListsContext';
import PlacePhoto from '../components/PlacePhoto';
import ListHeroPhoto from '../components/ListHeroPhoto';
import UserDetailsCard from '../components/UserDetailsCard';
import ReportModal from '../components/ReportModal';
import api from '../services/api';
import { splitPlaceAddress } from '../utils/locationUtils';
import { formatPriceLevel, getSubcategoryFromType } from '../services/googleMaps';
import { getOpenStatusInfo } from '../utils/openingHoursUtils';
import '../styles/PlaceDetails.css';

// Day names for weekly hours display
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Format relative time for last update
const formatLastUpdate = (timestamp) => {
  if (!timestamp) return null;

  // Handle Firestore Timestamp, ISO string, or Date object
  let date;
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  } else if (timestamp._seconds) {
    // Firestore Timestamp serialized format
    date = new Date(timestamp._seconds * 1000);
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    return null;
  }

  // Validate date
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'Updated recently';
  } else if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};


// Format weekly hours from opening hours data
const formatWeeklyHours = (openingHours) => {
  if (!openingHours?.periods) return null;

  // Check for 24/7
  if (openingHours.periods.length === 1) {
    const period = openingHours.periods[0];
    if (!period.close && period.open?.day === 0) {
      return DAY_NAMES.map((day) => ({ day, hours: 'Open 24 hours' }));
    }
  }

  // Use weekday_text if available (from Google API)
  if (openingHours.weekday_text?.length > 0) {
    // weekday_text starts with Monday (index 0), we want Sunday first
    const mondayFirst = openingHours.weekday_text;
    return [
      { day: 'Sunday', hours: mondayFirst[6]?.replace('Sunday: ', '') || 'Closed' },
      { day: 'Monday', hours: mondayFirst[0]?.replace('Monday: ', '') || 'Closed' },
      { day: 'Tuesday', hours: mondayFirst[1]?.replace('Tuesday: ', '') || 'Closed' },
      { day: 'Wednesday', hours: mondayFirst[2]?.replace('Wednesday: ', '') || 'Closed' },
      { day: 'Thursday', hours: mondayFirst[3]?.replace('Thursday: ', '') || 'Closed' },
      { day: 'Friday', hours: mondayFirst[4]?.replace('Friday: ', '') || 'Closed' },
      { day: 'Saturday', hours: mondayFirst[5]?.replace('Saturday: ', '') || 'Closed' },
    ];
  }

  // Build from periods
  const dayHours = {};
  for (let i = 0; i < 7; i++) {
    dayHours[i] = [];
  }

  openingHours.periods.forEach((period) => {
    if (period.open) {
      const openDay = period.open.day;
      const openTime = formatTimeFromPeriod(period.open);
      const closeTime = period.close ? formatTimeFromPeriod(period.close) : null;

      if (closeTime) {
        dayHours[openDay].push(`${openTime} - ${closeTime}`);
      } else {
        dayHours[openDay].push(`${openTime} - Close`);
      }
    }
  });

  return DAY_NAMES.map((day, index) => ({
    day,
    hours: dayHours[index].length > 0 ? dayHours[index].join(', ') : 'Closed',
  }));
};

// Format time from period object
const formatTimeFromPeriod = (timeObj) => {
  if (!timeObj) return '';

  let hour, minute;
  if (timeObj.hours !== undefined) {
    hour = timeObj.hours;
    minute = timeObj.minutes || 0;
  } else if (timeObj.time) {
    const time = parseInt(timeObj.time, 10);
    hour = Math.floor(time / 100);
    minute = time % 100;
  } else {
    return '';
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  if (minute === 0) {
    return `${displayHour} ${period}`;
  }
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

function PlaceDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authReady } = useAuth();
  const { canAccess } = useSubscription();
  const { getDesignation, ensureDesignationsLoaded } = useUserDesignations();
  const { getOpeningHours } = usePlaceStatusCache();
  const { lists: userLists, collaborationLists } = useLists();

  // Premium feature flags
  const canShowPhotos = canAccess('photos');
  const canShowOpeningHours = canAccess('openingHours');

  // Get data from navigation state
  const {
    place: initialPlace,
    places: initialPlaces,
    listId,
    listOwnerId,
    listOwnerUsername,
    listOwnerDisplayName,
    listOwnerProfilePhoto,
    isOwnList,
    isCollaborationList,
    isUserCollaborator,
    collaboratorIds = [],
  } = location.state || {};

  // State for current place (for next/prev navigation)
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (!initialPlaces || !initialPlace) return 0;
    return initialPlaces.findIndex(p => p.placeId === initialPlace.placeId) || 0;
  });

  // Current place based on index
  const places = initialPlaces || [];
  const basePlace = places[currentIndex] || initialPlace;

  // State for refreshed place data (overrides base place when refreshed)
  const [refreshedPlaceData, setRefreshedPlaceData] = useState(null);

  // Merge refreshed data with base place (refreshed data takes precedence)
  const place = refreshedPlaceData?.placeId === basePlace?.placeId
    ? { ...basePlace, ...refreshedPlaceData }
    : basePlace;

  // Ensure user designations are loaded (for displaying badges)
  useEffect(() => {
    ensureDesignationsLoaded();
  }, [ensureDesignationsLoaded]);

  // Reset refreshed data when navigating to a different place
  useEffect(() => {
    setRefreshedPlaceData(null);
  }, [currentIndex]);

  // Friends data state
  const [friendsData, setFriendsData] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);

  // Collaborator notes state
  const [collabNotes, setCollabNotes] = useState([]);
  const [collabNotesLoading, setCollabNotesLoading] = useState(false);

  // Collaborator designations state (badges)
  const [collabDesignations, setCollabDesignations] = useState([]);
  const [collabDesignationsLoading, setCollabDesignationsLoading] = useState(false);

  // Opening hours state
  const [openStatus, setOpenStatus] = useState(null);
  const [fullOpeningHours, setFullOpeningHours] = useState(null);

  // Followed lists with this place
  const [followedListsWithPlace, setFollowedListsWithPlace] = useState([]);
  const [followedListsLoading, setFollowedListsLoading] = useState(false);

  // Refresh modal state
  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [refreshEligibility, setRefreshEligibility] = useState(null);
  const [refreshEligibilityLoading, setRefreshEligibilityLoading] = useState(false);

  // Success notification state
  const [showRefreshSuccess, setShowRefreshSuccess] = useState(false);

  // Expandable sections state
  const [showAllFriends, setShowAllFriends] = useState(false);
  const [showAllCollabNotes, setShowAllCollabNotes] = useState(false);
  const [hoursExpanded, setHoursExpanded] = useState(false);

  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState(null);

  // User's own designation for this place
  const userDesignation = place?.placeId ? getDesignation(place.placeId) : null;
  // Determine if we should show owner's designation (when viewing someone else's list)
  const shouldUseOwnerDesignation = !isOwnList && !isUserCollaborator;


  // Parse address
  const { street, cityState } = useMemo(() => {
    if (!place) return { street: '', cityState: '' };
    return splitPlaceAddress(place.address, place.addressComponents);
  }, [place]);

  // Get formatted info
  const priceLevel = place ? formatPriceLevel(place.priceLevel) : null;
  const subcategory = place ? getSubcategoryFromType(place.placeType) : null;

  // Find user's lists that contain this place
  const userListsWithPlace = useMemo(() => {
    if (!place?.placeId) return [];

    const allUserLists = [...(userLists || []), ...(collaborationLists || [])];
    return allUserLists.filter(list => {
      // Don't show the current list
      if (list.id === listId) return false;
      // Check if this list contains the place
      return (list.items || []).some(item => item.placeId === place.placeId);
    });
  }, [place?.placeId, userLists, collaborationLists, listId]);

  // Fetch friends who saved this place
  useEffect(() => {
    if (!user || !authReady || !place?.placeId) return;

    const fetchFriendsData = async () => {
      setFriendsLoading(true);
      try {
        const response = await api.getFriendsPlaces([place.placeId]);
        setFriendsData(response.data?.[place.placeId] || []);
      } catch (error) {
        console.error('Failed to fetch friends data:', error);
        setFriendsData([]);
      } finally {
        setFriendsLoading(false);
      }
    };

    fetchFriendsData();
  }, [user, authReady, place?.placeId]);

  // Fetch collaborator notes (for collaboration lists)
  useEffect(() => {
    if (!user || !authReady || !isCollaborationList || !listId || !place?.placeId) {
      setCollabNotes([]);
      return;
    }

    const fetchCollabNotes = async () => {
      setCollabNotesLoading(true);
      try {
        const response = await api.getCollaboratorNotes(listId, place.placeId);
        setCollabNotes(response.data || []);
      } catch (error) {
        console.error('Failed to fetch collaborator notes:', error);
        setCollabNotes([]);
      } finally {
        setCollabNotesLoading(false);
      }
    };

    fetchCollabNotes();
  }, [user, authReady, isCollaborationList, listId, place?.placeId]);

  // Fetch collaborator designations (badges) for collaboration lists
  useEffect(() => {
    if (!user || !authReady || !isCollaborationList || !listId || !place?.placeId) {
      setCollabDesignations([]);
      return;
    }

    const fetchCollabDesignations = async () => {
      setCollabDesignationsLoading(true);
      try {
        const response = await api.getCollaboratorDesignations(listId, place.placeId);
        setCollabDesignations(response.data || []);
      } catch (error) {
        console.error('Failed to fetch collaborator designations:', error);
        setCollabDesignations([]);
      } finally {
        setCollabDesignationsLoading(false);
      }
    };

    fetchCollabDesignations();
  }, [user, authReady, isCollaborationList, listId, place?.placeId]);

  // Fetch followed lists that contain this place
  useEffect(() => {
    if (!user || !authReady || !place?.placeId) {
      setFollowedListsWithPlace([]);
      return;
    }

    const fetchFollowedLists = async () => {
      setFollowedListsLoading(true);
      try {
        const response = await api.getFollowedLists({ includePlaceData: 'heroPhotoOnly' });
        const lists = (response.data || [])
          .filter(item => item.list !== null)
          .map(item => item.list)
          .filter(list => {
            // Check if this list contains the place
            return (list.items || []).some(item => item.placeId === place.placeId);
          });
        setFollowedListsWithPlace(lists);
      } catch (error) {
        console.error('Failed to fetch followed lists:', error);
        setFollowedListsWithPlace([]);
      } finally {
        setFollowedListsLoading(false);
      }
    };

    fetchFollowedLists();
  }, [user, authReady, place?.placeId]);

  // Fetch opening hours status
  useEffect(() => {
    if (!canShowOpeningHours || !place?.placeId) {
      setOpenStatus(null);
      setFullOpeningHours(null);
      return;
    }

    const fetchOpenStatus = async () => {
      try {
        const result = await getOpeningHours(place.placeId, {
          storedOpeningHours: place.openingHours,
          openingHoursSavedAt: place.openingHoursSavedAt,
          savedAt: place.savedAt,
        });
        if (result) {
          setOpenStatus(getOpenStatusInfo(result, place.businessStatus));
          setFullOpeningHours(result);
        } else {
          setOpenStatus(getOpenStatusInfo(null, place.businessStatus));
          setFullOpeningHours(null);
        }
      } catch (error) {
        console.error('Failed to fetch opening hours:', error);
        setOpenStatus(null);
        setFullOpeningHours(null);
      }
    };

    fetchOpenStatus();
  }, [canShowOpeningHours, place?.placeId, place?.openingHours, place?.openingHoursSavedAt, place?.savedAt, place?.businessStatus, getOpeningHours]);

  // Handle back navigation - use browser back for clean history
  const handleBack = () => {
    navigate(-1);
  };

  // Handle next/prev navigation - update URL to reflect current place
  const handlePrev = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newPlace = places[newIndex];
      // Navigate to new place URL, replacing current entry to maintain clean history
      navigate(`/place/${newPlace.placeId}`, {
        replace: true,
        state: {
          ...location.state,
          place: newPlace,
        }
      });
      setCurrentIndex(newIndex);
    }
  };

  const handleNext = () => {
    if (currentIndex < places.length - 1) {
      const newIndex = currentIndex + 1;
      const newPlace = places[newIndex];
      // Navigate to new place URL, replacing current entry to maintain clean history
      navigate(`/place/${newPlace.placeId}`, {
        replace: true,
        state: {
          ...location.state,
          place: newPlace,
        }
      });
      setCurrentIndex(newIndex);
    }
  };

  // Handle manage place
  const handleManagePlace = async () => {
    // Determine source list info for analytics tracking (when saving from someone else's list)
    const sourceListInfo = (!isOwnList && !isUserCollaborator && listId) ? {
      sourceListId: listId,
      sourceListOwnerId: location.state?.listOwnerId || null,
    } : null;

    if (!place?.placeId) {
      navigate('/place/save', {
        replace: true,
        state: {
          place,
          existingListInfo: null,
          returnPath: location.pathname + location.search,
          returnState: location.state,
          sourceListInfo,
        }
      });
      return;
    }

    try {
      // Look up all lists that contain this place
      const response = await api.getUserPlacesByPlaceIds([place.placeId]);
      const existingListInfo = response.data?.[place.placeId] || [];
      navigate('/place/save', {
        replace: true,
        state: {
          place,
          existingListInfo,
          returnPath: location.pathname + location.search,
          returnState: location.state,
          sourceListInfo,
        }
      });
    } catch (error) {
      console.error('Failed to look up place lists:', error);
      // Navigate anyway with empty existing info
      navigate('/place/save', {
        replace: true,
        state: {
          place,
          existingListInfo: [],
          returnPath: location.pathname + location.search,
          returnState: location.state,
          sourceListInfo,
        }
      });
    }
  };

  // Handle open in app's map page
  const handleOpenInAppMap = () => {
    // Check both possible coordinate formats (lat/lng or coordinates.lat/lng)
    const lat = place?.lat ?? place?.coordinates?.lat;
    const lng = place?.lng ?? place?.coordinates?.lng;
    if (lat && lng) {
      // Update places array with refreshed data if applicable
      let updatedPlaces = initialPlaces;
      if (initialPlaces && refreshedPlaceData) {
        updatedPlaces = initialPlaces.map(p =>
          p.placeId === refreshedPlaceData.placeId ? { ...p, ...refreshedPlaceData } : p
        );
      }

      // Pass the current place (which includes refreshed data if any)
      navigate('/map', {
        state: {
          focusPlace: { ...place, lat, lng },
          returnPath: location.pathname + location.search,
          // Pass place data back so it's preserved when returning
          placeDetailsState: {
            ...location.state,
            place: place, // Use current merged place data
            places: updatedPlaces, // Updated places array with refreshed data
          },
        }
      });
    }
  };

  // Handle refresh modal - fetch eligibility when opening
  const handleOpenRefreshModal = async () => {
    setIsRefreshModalOpen(true);
    if (!place?.placeId) return;

    setRefreshEligibilityLoading(true);
    try {
      const response = await api.checkPlaceRefreshEligibility(place.placeId);
      setRefreshEligibility(response.data);
    } catch (error) {
      console.error('Failed to check refresh eligibility:', error);
      setRefreshEligibility(null);
    } finally {
      setRefreshEligibilityLoading(false);
    }
  };

  // Handle actual refresh
  const handleRefreshPlace = async () => {
    if (!place?.placeId) return;
    setRefreshLoading(true);
    try {
      const response = await api.enrichPlace(place.placeId, true); // forceRefresh = true
      // Update place data with refreshed data
      if (response.data && !response.data.skipped) {
        setRefreshedPlaceData({ ...response.data, placeId: place.placeId });
        // Also refresh opening hours display
        if (response.data.openingHours) {
          setFullOpeningHours(response.data.openingHours);
          setOpenStatus(getOpenStatusInfo(response.data.openingHours, response.data.businessStatus || place.businessStatus));
        }
      }
      setIsRefreshModalOpen(false);
      // Show success notification
      setShowRefreshSuccess(true);
      setTimeout(() => setShowRefreshSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to refresh place:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  // Handle list click
  const handleListClick = (listId) => {
    navigate(`/list/${listId}`);
  };

  // Handle report
  const handleReport = (data) => {
    setReportData({
      ...data,
      placeId: place?.placeId,
    });
    setReportModalOpen(true);
  };

  // Handle toggle hours expanded
  const handleToggleHours = () => {
    setHoursExpanded(!hoursExpanded);
  };

  // Redirect if no place data
  if (!place) {
    return (
      <div className="place-details-page no-bottom-nav">
        <div className="place-details-header">
          <button className="back-btn" onClick={handleBack} aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="header-title">Place Details</h1>
          <div className="header-spacer" />
        </div>
        <div className="place-details-content">
          <p className="empty-message">Place not found</p>
        </div>
      </div>
    );
  }

  const hasPrevious = places.length > 1 && currentIndex > 0;
  const hasNext = places.length > 1 && currentIndex < places.length - 1;
  const weeklyHours = fullOpeningHours ? formatWeeklyHours(fullOpeningHours) : null;
  const currentDay = new Date().getDay();

  // Get last update time from place data
  const lastUpdateTime = place.updatedAt || place.enrichedAt || place.createdAt;
  const lastUpdateFormatted = formatLastUpdate(lastUpdateTime);

  return (
    <div className="place-details-page no-bottom-nav">
      {/* Success Notification */}
      {showRefreshSuccess && (
        <div className="refresh-success-notification">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>Place data refreshed successfully</span>
        </div>
      )}

      {/* Header */}
      <div className="place-details-header">
        <button className="back-btn" onClick={handleBack} aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="header-title">Place Details</h1>
        <div className="header-spacer" />
      </div>

      {/* Content */}
      <div className="place-details-content">
        {/* Hero Section: Photo + Basic Info */}
        <div className="place-hero-section">
          {/* Photo (no badge overlay - per new design) */}
          <div className="place-photo-section">
            {canShowPhotos ? (
              <PlacePhoto
                placeId={place.placeId}
                placeName={place.name}
                storedPhotoUrl={place.photoUrl}
                photoUrlSavedAt={place.photoUrlSavedAt}
                className="place-photo-large"
                loadingClassName="place-photo-large loading"
                errorClassName="place-photo-large placeholder"
              />
            ) : (
              <div className="place-photo-large placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
            )}
          </div>

          {/* Place Name */}
          <div className="place-title-section">
            <h2 className="place-name">{place.name}</h2>
          </div>

          {/* Address */}
          <div className="place-address">
            <svg className="info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <div className="address-text">
              {street && <span>{street}</span>}
              {street && cityState && ', '}
              {cityState && <span className="city-state">{cityState}</span>}
            </div>
          </div>

          {/* Tags */}
          <div className="place-tags">
            {place.category && <span className="place-tag category">{place.category}</span>}
            {subcategory && <span className="place-tag subcategory">{subcategory}</span>}
            {priceLevel && <span className="place-tag price">{priceLevel}</span>}
          </div>

          {/* Action Buttons - Equal width with labels */}
          {/* Hide Edit/Save if place is already on user's list (userDesignation exists) */}
          <div className="hero-actions">
            {!userDesignation && (
              <button className="hero-action-btn with-label" onClick={handleManagePlace}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>Save</span>
              </button>
            )}
            <button className="hero-action-btn with-label" onClick={handleOpenInAppMap}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>View on Map</span>
            </button>
            <button className="hero-action-btn with-label" onClick={handleOpenRefreshModal}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M23 4v6h-6"/>
                <path d="M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              <span>Refresh</span>
            </button>
          </div>

        </div>

        {/* Hours Section - Collapsible */}
        {canShowOpeningHours && (weeklyHours || openStatus?.isPermanentlyClosed || openStatus?.isTemporarilyClosed) && (
          <div className="detail-section hours-section">
            <button className="hours-header-btn" onClick={handleToggleHours} disabled={!weeklyHours}>
              <div className="hours-header-left">
                <h3 className="section-title">Hours</h3>
                {openStatus && (
                  <span className={`hours-status ${openStatus.status}`}>
                    {openStatus.status === 'open' && openStatus.closesAt && `Closes ${openStatus.closesAt}`}
                    {openStatus.status === 'open' && !openStatus.closesAt && 'Open'}
                    {openStatus.status === 'closing-soon' && openStatus.closesAt && `Closes ${openStatus.closesAt}`}
                    {openStatus.status === 'closed' && !openStatus.isTemporarilyClosed && openStatus.opensAt && `Opens ${openStatus.opensDay || ''} ${openStatus.opensAt}`}
                    {openStatus.status === 'closed' && !openStatus.isTemporarilyClosed && !openStatus.opensAt && 'Closed'}
                    {openStatus.isTemporarilyClosed && 'Temporarily Closed'}
                    {openStatus.status === 'permanently-closed' && 'Permanently Closed'}
                  </span>
                )}
              </div>
              {weeklyHours && (
                <svg
                  className={`expand-icon ${hoursExpanded ? 'expanded' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
            </button>
            {hoursExpanded && weeklyHours && (
              <div className="weekly-hours">
                {weeklyHours.map(({ day, hours }, index) => (
                  <div key={day} className={`hours-row ${index === currentDay ? 'today' : ''}`}>
                    <span className="day-name">{DAY_NAMES_SHORT[index]}</span>
                    <span className="day-hours">{hours}</span>
                  </div>
                ))}
                <p className="hours-disclaimer">Hours may vary on holidays or change unexpectedly.</p>
              </div>
            )}
          </div>
        )}

        {/* Your Take Section - always visible with edit button */}
        {userDesignation && (
          <div className="detail-section">
            <h3 className="section-title">Your Take</h3>
            <UserDetailsCard
              user={{
                userId: user?.uid,
                username: user?.username,
                displayName: user?.displayName,
                profilePhoto: user?.photoURL,
              }}
              designation={userDesignation}
              placeId={place?.placeId}
              isCurrentUser={true}
              showAvatar={false}
              showMenu={false}
              onEdit={handleManagePlace}
            />
          </div>
        )}

        {/* Your Lists with This Place */}
        {userListsWithPlace.length > 0 && (
          <div className="detail-section">
            <h3 className="section-title">
              Your Lists
              <span className="count">({userListsWithPlace.length})</span>
            </h3>
            <div className="lists-horizontal-scroll">
              {userListsWithPlace.map((list) => {
                const heroItem = (list.items || []).find(item => item.placeId);
                return (
                  <div
                    key={list.id}
                    className="mini-list-card"
                    onClick={() => handleListClick(list.id)}
                  >
                    <div className="mini-list-photo">
                      <ListHeroPhoto
                        placeId={heroItem?.placeId}
                        altText={list.title || list.name}
                        storedPhotoUrl={heroItem?.photoUrl}
                        customPhoto={list.customPhoto}
                      />
                    </div>
                    <div className="mini-list-info">
                      <span className="mini-list-title">{list.title || list.name}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* List Owner's Details - show when viewing someone else's list */}
        {shouldUseOwnerDesignation && place?.ownerDesignation && (
          <div className="detail-section">
            <h3 className="section-title">{listOwnerDisplayName ? `${listOwnerDisplayName}'s Take` : "Owner's Take"}</h3>
            <UserDetailsCard
              user={{
                userId: listOwnerId,
                username: listOwnerUsername,
                displayName: listOwnerDisplayName,
                profilePhoto: listOwnerProfilePhoto,
              }}
              designation={place.ownerDesignation}
              placeId={place?.placeId}
              placeName={place?.name}
              isCurrentUser={false}
              showAvatar={true}
              showMenu={true}
              onReport={handleReport}
            />
          </div>
        )}

        {/* Collaborators Section - for collaboration lists using UserDetailsCard */}
        {isCollaborationList && (() => {
          // Filter out current user's data since we show "Your Details" above
          const otherCollabNotes = collabNotes.filter(note => !note.isCurrentUser);
          const hasCollabData = collabDesignations.length > 0 || otherCollabNotes.length > 0;
          const isLoading = collabDesignationsLoading || collabNotesLoading;

          if (!isLoading && !hasCollabData) return null;

          // Create combined list with ratings and notes merged
          const combinedCollabData = collabDesignations
            .filter(d => d.userId !== user?.uid) // Exclude current user
            .map(designation => {
              const matchingNote = otherCollabNotes.find(note => note.userId === designation.userId);
              return {
                ...designation,
                notes: matchingNote?.notes || null,
              };
            });

          // Add any notes from users not in designations
          otherCollabNotes.forEach(note => {
            const hasDesignation = combinedCollabData.some(d => d.userId === note.userId);
            if (!hasDesignation && note.userId !== user?.uid) {
              combinedCollabData.push({
                userId: note.userId,
                username: note.username,
                displayName: note.displayName,
                profilePhoto: note.profilePhoto,
                badge: null,
                notes: note.notes,
                socialLinks: note.socialLinks || [],
              });
            }
          });

          return (
            <div className="detail-section">
              <h3 className="section-title">
                Collaborators
                {combinedCollabData.length > 0 && <span className="count">({combinedCollabData.length})</span>}
              </h3>
              {isLoading ? (
                <div className="loading-indicator">
                  <div className="spinner" />
                  <span>Loading...</span>
                </div>
              ) : (
                <>
                  <div className="user-details-list">
                    {(showAllCollabNotes ? combinedCollabData : combinedCollabData.slice(0, 3)).map((collab, index) => (
                      <UserDetailsCard
                        key={collab.userId || index}
                        user={{
                          userId: collab.userId,
                          username: collab.username,
                          displayName: collab.displayName,
                          profilePhoto: collab.profilePhoto,
                        }}
                        designation={{
                          badge: collab.badge,
                          avoidReason: collab.avoidReason,
                          notes: collab.notes,
                          socialLinks: collab.socialLinks || [],
                        }}
                        placeId={place?.placeId}
                        placeName={place?.name}
                        isCurrentUser={false}
                        showAvatar={true}
                        showMenu={true}
                        onReport={handleReport}
                      />
                    ))}
                  </div>
                  {combinedCollabData.length > 3 && (
                    <button
                      className="show-more-btn"
                      onClick={() => setShowAllCollabNotes(!showAllCollabNotes)}
                    >
                      {showAllCollabNotes ? 'Show less' : `Show ${combinedCollabData.length - 3} more`}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Connections Who Saved This - with notes and social links via UserDetailsCard */}
        {(() => {
          // Filter out collaborators from friends list when viewing a collaboration list
          // Also filter out the list owner when viewing their list (to avoid duplication with Owner's Take)
          let filteredFriends = friendsData;
          if (isCollaborationList) {
            filteredFriends = friendsData.filter(friend =>
              !collaboratorIds.includes(friend.userId) && friend.userId !== listOwnerId
            );
          } else if (listOwnerId) {
            // Filter out list owner when viewing their list
            filteredFriends = friendsData.filter(friend => friend.userId !== listOwnerId);
          }
          const hasFilteredFriends = filteredFriends.length > 0;

          if (!friendsLoading && !hasFilteredFriends) return null;

          return (
            <div className="detail-section">
              <h3 className="section-title">
                Connections Who Saved This
                {hasFilteredFriends && <span className="count">({filteredFriends.length})</span>}
              </h3>
              {friendsLoading ? (
                <div className="loading-indicator">
                  <div className="spinner" />
                  <span>Loading...</span>
                </div>
              ) : (
                <>
                  <div className="user-details-list">
                    {(showAllFriends ? filteredFriends : filteredFriends.slice(0, 3)).map((friend, index) => (
                      <UserDetailsCard
                        key={friend.userId || index}
                        user={{
                          userId: friend.userId,
                          username: friend.username,
                          displayName: friend.name,
                          profilePhoto: friend.profilePhoto,
                        }}
                        designation={{
                          badge: friend.badge,
                          avoidReason: friend.avoidReason,
                          notes: friend.notes,
                          socialLinks: friend.socialLinks || [],
                        }}
                        placeId={place?.placeId}
                        placeName={place?.name}
                        isCurrentUser={false}
                        showAvatar={true}
                        showMenu={true}
                        clickableProfile={false}
                        onReport={handleReport}
                      />
                    ))}
                  </div>
                  {filteredFriends.length > 3 && (
                    <button
                      className="show-more-btn"
                      onClick={() => setShowAllFriends(!showAllFriends)}
                    >
                      {showAllFriends ? 'Show less' : `Show ${filteredFriends.length - 3} more`}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Followed Lists with This Place - directly after Connections */}
        {(followedListsLoading || followedListsWithPlace.length > 0) && (
          <div className="detail-section">
            <h3 className="section-title">
              Lists You Follow
              {followedListsWithPlace.length > 0 && <span className="count">({followedListsWithPlace.length})</span>}
            </h3>
            {followedListsLoading ? (
              <div className="loading-indicator">
                <div className="spinner" />
                <span>Loading...</span>
              </div>
            ) : (
              <div className="lists-horizontal-scroll">
                {followedListsWithPlace.map((list) => {
                  const heroItem = (list.items || []).find(item => item.placeId);
                  return (
                    <div
                      key={list.id}
                      className="mini-list-card"
                      onClick={() => handleListClick(list.id)}
                    >
                      <div className="mini-list-photo">
                        <ListHeroPhoto
                          placeId={heroItem?.placeId}
                          altText={list.title || list.name}
                          storedPhotoUrl={heroItem?.photoUrl}
                          customPhoto={list.customPhoto}
                        />
                      </div>
                      <div className="mini-list-info">
                        <span className="mini-list-title">{list.title || list.name}</span>
                        <span className="mini-list-meta">
                          by {list.creatorName || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Premium Info: Phone, Rating, Website, Google Maps */}
        {(canShowOpeningHours && (place.googleRating || place.phoneNumber || place.websiteUrl)) || place.googleMapsUrl ? (
          <div className="detail-section">
            <h3 className="section-title">Details</h3>
            <div className="place-details-grid">
              {/* Rating row with Google Maps link */}
              {(canShowOpeningHours && place.googleRating) || place.googleMapsUrl ? (
                <div className={`detail-item rating-row ${!(canShowOpeningHours && place.googleRating) ? 'maps-only' : ''}`}>
                  {canShowOpeningHours && place.googleRating ? (
                    <div className="rating-info">
                      <svg className="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <div className="detail-text">
                        <span className="detail-value">{place.googleRating.toFixed(1)}</span>
                        {place.googleRatingCount && (
                          <span className="detail-subtext">({place.googleRatingCount.toLocaleString()} reviews)</span>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {place.googleMapsUrl && (
                    <a href={place.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="google-maps-link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                      <span>Open in Google Maps</span>
                    </a>
                  )}
                </div>
              ) : null}
              {canShowOpeningHours && place.phoneNumber && (
                <a href={`tel:${place.phoneNumber}`} className="detail-item clickable">
                  <svg className="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span className="detail-value">{place.phoneNumber}</span>
                </a>
              )}
              {canShowOpeningHours && place.websiteUrl && (
                <a href={place.websiteUrl} target="_blank" rel="noopener noreferrer" className="detail-item clickable">
                  <svg className="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  <span className="detail-value website-link">Visit Website</span>
                </a>
              )}
            </div>
          </div>
        ) : null}

      </div>

      {/* Next/Previous Navigation */}
      {places.length > 1 && (
        <div className="place-navigation">
          <button
            className="nav-btn prev"
            onClick={handlePrev}
            disabled={!hasPrevious}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Previous
          </button>
          <span className="nav-position">{currentIndex + 1} of {places.length}</span>
          <button
            className="nav-btn next"
            onClick={handleNext}
            disabled={!hasNext}
          >
            Next
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      {/* Refresh Modal */}
      {isRefreshModalOpen && (
        <div className="modal-overlay" onClick={() => !refreshLoading && setIsRefreshModalOpen(false)}>
          <div className="modal-content refresh-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Refresh Place Data</h2>
              {!refreshLoading && (
                <button className="btn-close" onClick={() => setIsRefreshModalOpen(false)}>✕</button>
              )}
            </div>
            <div className="modal-body">
              {refreshLoading ? (
                <div className="refresh-loading">
                  <div className="refresh-icon spinning">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="M23 4v6h-6"/>
                      <path d="M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                  </div>
                  <h3>Refreshing...</h3>
                  <p>Fetching latest data for this place</p>
                </div>
              ) : refreshEligibilityLoading ? (
                <div className="refresh-loading">
                  <div className="refresh-icon spinning">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="M23 4v6h-6"/>
                      <path d="M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                  </div>
                  <h3>Checking...</h3>
                  <p>Checking refresh eligibility</p>
                </div>
              ) : !refreshEligibility?.isPremium ? (
                <div className="refresh-info">
                  <div className="refresh-icon disabled">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="M23 4v6h-6"/>
                      <path d="M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                  </div>
                  <h3>Premium Feature</h3>
                  <p>Refreshing place data is available to Premium subscribers.</p>
                  <div className="refresh-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setIsRefreshModalOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                  {lastUpdateFormatted && (
                    <p className="last-update-text">Last updated: {lastUpdateFormatted}</p>
                  )}
                </div>
              ) : !refreshEligibility?.canRefresh ? (
                <div className="refresh-info">
                  <div className="refresh-icon disabled">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="M23 4v6h-6"/>
                      <path d="M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                  </div>
                  <h3>Refresh Not Available</h3>
                  <p>
                    {refreshEligibility?.reason === 'Recently refreshed' && refreshEligibility?.hoursRemaining > 0
                      ? `This place was recently refreshed. You can refresh again in ${refreshEligibility.hoursRemaining} hour${refreshEligibility.hoursRemaining === 1 ? '' : 's'}.`
                      : refreshEligibility?.reason === 'Rate limited' && refreshEligibility?.daysRemaining > 0
                      ? `You can refresh this place again in ${refreshEligibility.daysRemaining} day${refreshEligibility.daysRemaining === 1 ? '' : 's'}.`
                      : 'This place cannot be refreshed right now.'}
                  </p>
                  <div className="refresh-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setIsRefreshModalOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                  {lastUpdateFormatted && (
                    <p className="last-update-text">Last updated: {lastUpdateFormatted}</p>
                  )}
                </div>
              ) : (
                <div className="refresh-info">
                  <div className="refresh-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="M23 4v6h-6"/>
                      <path d="M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                  </div>
                  <p>This will refresh the opening hours, photos, and other details for this place from Google.</p>
                  <div className="refresh-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setIsRefreshModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={handleRefreshPlace}
                    >
                      Refresh Now
                    </button>
                  </div>
                  {lastUpdateFormatted && (
                    <p className="last-update-text">Last updated: {lastUpdateFormatted}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setReportData(null);
        }}
        reportedUserId={reportData?.reportedUserId}
        contentType={reportData?.contentType}
        content={reportData?.content}
        placeId={reportData?.placeId}
        socialLinks={reportData?.socialLinks || []}
      />
    </div>
  );
}

export default PlaceDetails;
